"use client";

import { useEffect, useState } from "react";
import { onSnapshot, query, where } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import {
  instantSubmissionSchema,
  type InstantSubmission,
  type InstantSubmissionStatus,
} from "@/lib/schemas";

type State = {
  submissions: InstantSubmission[];
  loading: boolean;
  error: string | null;
};

type Opts = {
  status?: InstantSubmissionStatus;
  submittedBy?: string;
};

const cache = new Map<string, InstantSubmission[]>();
const cacheKey = (hid: string, opts: Opts) =>
  `${hid}|${opts.status ?? "*"}|${opts.submittedBy ?? "*"}`;

export function useInstantSubmissions(
  householdId: string | undefined,
  opts: Opts = {},
): State {
  const { status, submittedBy } = opts;
  const key = householdId
    ? cacheKey(householdId, { status, submittedBy })
    : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    submissions: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const constraints = [];
    if (status) constraints.push(where("status", "==", status));
    if (submittedBy)
      constraints.push(where("submittedBy", "==", submittedBy));
    const q = query(
      refCol(db, paths.instantSubmissions(householdId)),
      ...constraints,
    );
    const k = cacheKey(householdId, { status, submittedBy });
    const unsub = onSnapshot(
      q,
      (snap) => {
        const submissions = snap.docs
          .flatMap((d) => {
            const parsed = instantSubmissionSchema.safeParse({
              id: d.id,
              ...d.data(),
            });
            return parsed.success ? [parsed.data] : [];
          })
          .sort((a, b) => b.submittedAt.toMillis() - a.submittedAt.toMillis());
        cache.set(k, submissions);
        setState({ submissions, loading: false, error: null });
      },
      (err) =>
        setState({ submissions: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId, status, submittedBy]);

  return state;
}
